import React from 'react';
import type { PortfolioItem } from '../types/interfaces';

interface PortfolioItemProps {
  item: PortfolioItem;
  index: number;
  onRemove: (index: number) => void;
}

function formatNumber(num: number): string {
  if (num >= 1e12) return `${(num / 1e12).toFixed(2)}T`;
  if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
  if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
  if (num >= 1e3) return `${(num / 1e3).toFixed(2)}K`;
  return num.toFixed(2);
}

function formatCurrency(num: number): string {
  if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
  if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
  if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
  if (num >= 1e3) return `$${(num / 1e3).toFixed(2)}K`;
  return `$${num.toFixed(2)}`;
}

export default function PortfolioItemComponent({ item, index, onRemove }: PortfolioItemProps) {
  const currentPrice = item.currentPrice || 0;
  const purchasePrice = item.purchasePrice || currentPrice;
  const quantity = item.quantity || 0;
  const value = quantity * currentPrice;
  const cost = quantity * purchasePrice;
  const profit = value - cost;
  const profitPercent = cost > 0 ? (profit / cost) * 100 : 0;
  const change24h = item.change24h || 0;
  const change24hValue = value * (change24h / 100);

  return (
    <div className="group bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-3xl p-6 flex items-center justify-between gap-6 relative shadow-bubble border-2 border-transparent hover:border-primary/30 transition-all duration-300 hover:scale-[1.02] hover:shadow-bubble-lg">
      <div className="flex items-center gap-5 flex-1">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-purple-500/20 flex items-center justify-center text-3xl shadow-lg border-2 border-primary/30">
          💎
        </div>
        <div className="flex-1">
          <div className="text-xl font-bold mb-1.5 group-hover:text-primary transition-colors">{item.name}</div>
          <div className="text-text-secondary text-sm font-medium uppercase tracking-wider">
            {formatNumber(quantity)} {item.symbol.toUpperCase()}
          </div>
        </div>
      </div>
      <div className="text-right">
        <div className="text-2xl font-bold mb-2 bg-gradient-to-r from-text to-primary bg-clip-text text-transparent">{formatCurrency(value)}</div>
        <div className={`text-sm font-bold px-4 py-1.5 rounded-full inline-block mb-2 ${
          profit >= 0 
            ? 'text-green-400 bg-gradient-to-r from-green-500/20 to-green-600/20 border border-green-500/30 shadow-glow-green' 
            : 'text-red-400 bg-gradient-to-r from-red-500/20 to-red-600/20 border border-red-500/30'
        }`}>
          {profit >= 0 ? '↗' : '↘'} {profit >= 0 ? '+' : ''}{formatCurrency(profit)} ({profitPercent >= 0 ? '+' : ''}
          {profitPercent.toFixed(2)}%)
        </div>
        <div className={`text-xs font-medium px-3 py-1 rounded-full inline-block ${
          change24h >= 0 
            ? 'text-green-400 bg-green-500/10' 
            : 'text-red-400 bg-red-500/10'
        }`}>
          24h: {change24h >= 0 ? '↗' : '↘'} {change24h >= 0 ? '+' : ''}{change24h.toFixed(2)}% (
          {change24hValue >= 0 ? '+' : ''}{formatCurrency(Math.abs(change24hValue))})
        </div>
      </div>
      <button
        onClick={() => onRemove(index)}
        className="px-6 py-3 bg-gradient-to-r from-danger to-red-600 border-none rounded-full text-white text-sm font-bold cursor-pointer transition-all duration-300 hover:scale-110 hover:shadow-bubble-lg active:scale-95"
      >
        🗑️ Remove
      </button>
    </div>
  );
}

